import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  supabase,
  handleSupabaseError,
  handleUnexpectedError,
  getBaliDayBoundsUtc,
  baliDateKeysEndingToday,
  baliDayBoundsForKey,
  isCheckInCooldownError,
  CHECK_IN_COOLDOWN_MESSAGE,
  CHECK_IN_STATS_CONFIG,
  fetchAllRows,
} from '@/lib';
import { CheckIn } from '@/types';
import { logActivity } from '@/utils';
import { CHECK_IN_QUERY_KEYS, DASHBOARD_QUERY_KEYS } from '@/constants';

/**
 * How far back the check-ins table reads. `null` means all time.
 *
 * This exists because `check_ins` is the fastest-growing table in the app —
 * ~211 rows/day, 6,881 total on 2026-08-14 — and the list used to be an
 * unbounded select. PostgREST capped it at 1,000 rows without erroring, so the
 * page silently showed only the newest ~3 days (back to 2026-08-11) with 5,853
 * check-ins hidden, and the member filter, which runs client-side over whatever
 * was loaded, could not find anything older.
 *
 * A window alone is not sufficient: even 7 days is 1,752 rows, already past the
 * cap. So every window is ALSO paged. The window is about payload, the paging
 * is about correctness — don't remove either thinking the other covers it.
 */
export const CHECK_IN_RANGE_DAYS = [7, 30, 90] as const;
export const DEFAULT_CHECK_IN_RANGE_DAYS = 7;

export interface CheckInsQueryOptions {
  /** Bali calendar days back from today, inclusive of today. `null` = all time. */
  days?: number | null;
  /** Narrow to one member, server-side. */
  memberId?: string | null;
}

export function useCheckIns({ days = DEFAULT_CHECK_IN_RANGE_DAYS, memberId = null }: CheckInsQueryOptions = {}) {
  return useQuery({
    queryKey: [CHECK_IN_QUERY_KEYS.ALL, days, memberId],
    queryFn: async () => {
      try {
        // Anchored on the Bali calendar day, matching how every count on this
        // page is already queried — a browser-local midnight would put the
        // boundary in the wrong place for an admin outside UTC+8.
        const startUtc =
          days == null ? null : baliDayBoundsForKey(baliDateKeysEndingToday(days)[0]).startUtc;

        const { data, error } = await fetchAllRows<CheckIn>(() => {
          let query = supabase
            .from('check_ins')
            .select(`
              *,
              member:members!inner(*)
            `)
            .filter('member.deleted_at', 'is', null)
            .order('timestamp', { ascending: false });

          if (startUtc) query = query.gte('timestamp', startUtc);
          if (memberId) query = query.eq('member_id', memberId);

          return query;
        });

        if (error) handleSupabaseError(error, { operation: 'fetching check-ins' });

        return data;
      } catch (err) {
        handleUnexpectedError(err, { hookName: 'useCheckIns', operation: 'loading check-ins' });
      }
    },
  });
}

export function useCheckInCountInTheHouse() {
  return useQuery({
    queryKey: [CHECK_IN_QUERY_KEYS.IN_THE_HOUSE],
    queryFn: async () => {
      // A rolling instant window ("who is on the floor right now"), so this one
      // needs no Bali anchoring — but it must be real elapsed time. Subtracting
      // via `setMinutes` does field arithmetic on the BROWSER's local calendar,
      // so for an admin in a DST zone the twice-yearly transition turns this
      // into a 0- or 120-minute window. Epoch milliseconds have no such hole.
      const cutoff = new Date(
        Date.now() - CHECK_IN_STATS_CONFIG.IN_THE_HOUSE_MINUTES * 60_000
      ).toISOString();

      const { count, error } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', cutoff);

      if (error) handleSupabaseError(error, { operation: 'fetching in-the-house check-in count' });
      return count || 0;
    },
  });
}

export function useCheckInCountLast30Days() {
  return useQuery({
    queryKey: [CHECK_IN_QUERY_KEYS.LAST_30_DAYS],
    queryFn: async () => {
      // 30 whole Bali calendar days ending with today, matching how every other
      // 30-day window in the app is built (useDailyRevenueLast30Days,
      // useMembershipsReport). The previous rolling now-minus-720h window was
      // anchored to the moment of page load, so the same day's number drifted
      // and disagreed with the Bali-bucketed "Checked-In Today" tile beside it.
      const dayKeys = baliDateKeysEndingToday(CHECK_IN_STATS_CONFIG.LAST_30_DAYS);
      const { startUtc } = baliDayBoundsForKey(dayKeys[0]);
      const { endUtc } = baliDayBoundsForKey(dayKeys[dayKeys.length - 1]);

      const { count, error } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', startUtc)
        .lt('timestamp', endUtc);

      if (error) handleSupabaseError(error, { operation: 'fetching last 30 days check-in count' });
      return count || 0;
    },
  });
}

export function useCheckInCountToday() {
  return useQuery({
    queryKey: [CHECK_IN_QUERY_KEYS.TODAY],
    queryFn: async () => {
      // "Today" is the Bali calendar day — the business operates in Asia/Makassar,
      // so all bucketing of check-in timestamps must be anchored there, not to
      // the admin's browser timezone.
      const { startUtc, endUtc } = getBaliDayBoundsUtc();

      const { count, error } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', startUtc)
        .lt('timestamp', endUtc);

      if (error) handleSupabaseError(error, { operation: 'fetching today check-in count' });
      return count || 0;
    },
  });
}

export function useMemberCheckedInToday(memberId: string | undefined) {
  return useQuery({
    queryKey: [CHECK_IN_QUERY_KEYS.TODAY, 'member', memberId],
    enabled: Boolean(memberId),
    queryFn: async () => {
      const { startUtc, endUtc } = getBaliDayBoundsUtc();

      const { count, error } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .eq('member_id', memberId!)
        .gte('timestamp', startUtc)
        .lt('timestamp', endUtc);

      if (error) handleSupabaseError(error, { operation: 'checking member today check-in' });
      return count || 0;
    },
  });
}

export function useCheckInCountTotal() {
  return useQuery({
    // ALL is the root 'check-ins' string, so this key is a child of it and the
    // existing [CHECK_IN_QUERY_KEYS.ALL] invalidations prefix-match it. This
    // read CHECK_IN_QUERY_KEYS.BASE, which has never existed on that object —
    // the key was literally [undefined, 'total'], matched by no invalidation,
    // so the total on CheckInsPage never moved after a check-in.
    queryKey: [CHECK_IN_QUERY_KEYS.ALL, 'total'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true });

      if (error) handleSupabaseError(error, { operation: 'fetching total check-in count' });
      return count || 0;
    },
  });
}

export function useDeleteCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (checkIn: CheckIn) => {
      const { error } = await supabase
        .from('check_ins')
        .delete()
        .eq('id', checkIn.id);

      if (error) handleSupabaseError(error, { operation: 'deleting check-in' });

      logActivity(
        'check_in_deleted',
        'check_in',
        checkIn.id,
        {
          timestamp: { from: checkIn.timestamp, to: null },
          member_id: { from: checkIn.member_id, to: null },
          checked_in_by_name: { from: checkIn.checked_in_by_name, to: null },
          checked_in_by_email: { from: checkIn.checked_in_by_email, to: null },
        },
        {
          member_name: checkIn.member
            ? `${checkIn.member.first_name} ${checkIn.member.last_name}`
            : 'Unknown Member',
          checked_in_by_name: checkIn.checked_in_by_name,
          checked_in_by_email: checkIn.checked_in_by_email,
        },
        checkIn.member_id
      );

      return checkIn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.ALL] });
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.IN_THE_HOUSE] });
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.LAST_30_DAYS] });
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.TODAY] });
      // The dashboard counts today's check-ins under its own key, which is a
      // different string from CHECK_IN_QUERY_KEYS.TODAY and so isn't covered by
      // the line above. The check-in modal is hosted in Layout, so this fires
      // while the admin is looking at the Dashboard tile it feeds.
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.TODAY_CHECK_INS_COUNT] });
    },
  });
}

export function useCreateCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      memberId, 
      memberName,
      memberEmail 
    }: { 
      memberId: string; 
      memberName: string;
      memberEmail: string;
    }) => {
      try {
        // Get current admin user details
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('full_name, email')
          .eq('id', user.id)
          .single();

        if (adminError) throw new Error('Failed to get admin details');

        // Create check-in record
        const { data, error } = await supabase
          .from('check_ins')
          .insert([{
            member_id: memberId,
            timestamp: new Date().toISOString(),
            checked_in_by_name: adminData.full_name,
            checked_in_by_email: adminData.email,
          }])
          .select()
          .single();

        if (error) {
          if (isCheckInCooldownError(error)) {
            throw new Error(CHECK_IN_COOLDOWN_MESSAGE);
          }
          throw error;
        }

        if (!data) {
          throw new Error('Failed to create check-in');
        }

        // Log the activity
        logActivity(
          'member_checked_in',
          'check_in',
          data.id,
          {
            timestamp: { from: null, to: data.timestamp }
          },
          {
            member_id: memberId,
            member_name: memberName,
            member_email: memberEmail,
            checked_in_by_admin: true
          },
          memberId
        );

        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useCreateCheckIn', operation: 'creating check-in' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.ALL] });
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.IN_THE_HOUSE] });
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.LAST_30_DAYS] });
      queryClient.invalidateQueries({ queryKey: [CHECK_IN_QUERY_KEYS.TODAY] });
      // The dashboard counts today's check-ins under its own key, which is a
      // different string from CHECK_IN_QUERY_KEYS.TODAY and so isn't covered by
      // the line above. The check-in modal is hosted in Layout, so this fires
      // while the admin is looking at the Dashboard tile it feeds.
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.TODAY_CHECK_INS_COUNT] });
    },
  });
} 